import { useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  apiErrorText,
  apiLoadErrorText,
  useExportPrcXlsxMutation,
  useGetPrcModelHistoryQuery,
  useGetPrcModelResultQuery,
  type PrcModelRow,
  type PrcVersion,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerFooter,
  DrawerHeader,
  DrawerIconButton,
} from '@shared/ui/Drawer';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { IconChevronDown, IconChevronRight, IconDownload } from '@shared/ui/icons';
import {
  BlockHeading,
  BlockSection,
  FleetChip,
  Matrix,
  PassportView,
  ScrollRegion,
  fleetLeading,
  fleetType,
  typo,
} from '@entities/fleet';
import { formatIsoDate } from '@entities/prc';
import { Notice } from '@widgets/FleetRegistry/ui';
import { HeaderMeta, Eyebrow, DrawerTitle } from '../drawerParts';
import {
  formatPercent,
  formatServerMoney,
  personName,
  safeFileName,
  saveObjectUrl,
} from '../format';
import { Stack, StatGrid, SchemeScope } from '../ui';
import { ArticlesByYear, IntermediateValues } from './modelParts';

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  min-height: 36px;

  & > ${Eyebrow} {
    flex: 1 1 auto;
    min-width: 0;
  }
`;

const MetaRow = styled(HeaderMeta)`
  padding-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Position = styled.span`
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const PrevIcon = styled(IconChevronRight)`
  transform: rotate(180deg);
`;

const Snapshot = styled.details`
  margin-top: ${({ theme }) => theme.spacing(6)};
  border-top: 1px solid ${({ theme }) => theme.colors.borderStrong};

  & > summary {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(2)};
    padding: ${({ theme }) => `${theme.spacing(4)} 0`};
    font-size: ${fleetType.lead};
    font-weight: 600;
    line-height: ${fleetLeading.tight};
    list-style: none;
    cursor: pointer;
  }

  & > summary::-webkit-details-marker {
    display: none;
  }

  & > summary > svg {
    margin-left: auto;
    transition: transform ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};
  }

  &[open] > summary > svg {
    transform: rotate(180deg);
  }

  & > p {
    margin: 0 0 ${({ theme }) => theme.spacing(2)};
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const WarnList = styled.ul`
  flex: 1 1 100%;
  margin: 0;
  padding-left: ${({ theme }) => theme.spacing(5)};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
`;

const LinkButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  height: 40px;
  padding: ${({ theme }) => `0 ${theme.spacing(4)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const FooterActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  width: 100%;

  & > .spacer {
    flex: 1 1 auto;
  }

  @container drawer (max-width: 520px) {
    & > * {
      flex: 1 1 100%;
      justify-content: center;
    }

    & > .spacer {
      display: none;
    }
  }
`;

/** Адрес печатного вида «Калькуляции (форма)» по машине. */
export function printPath(
  versionId: string,
  modelId: string,
  opts: { year?: number; variant?: 1 | 2; sensitivity?: string; conditions?: string } = {},
): string {
  // print=1 — печатный вид сразу открывает диалог печати.
  const p = new URLSearchParams({ model: modelId, print: '1' });
  if (opts.year !== undefined) p.set('year', String(opts.year));
  if (opts.variant !== undefined) p.set('variant', String(opts.variant));
  if (opts.sensitivity) p.set('sensitivity', opts.sensitivity);
  if (opts.conditions) p.set('cond', opts.conditions);
  return `/agents/econ/prc/${versionId}/print?${p.toString()}`;
}

/** Кнопки формы по машине: Excel и печать — в панели модели и на «Безубыточности». */
export function FormButtons({
  version,
  modelId,
  modelName,
  print,
}: {
  version: PrcVersion;
  modelId: string;
  modelName: string;
  /** `conditions` — вариант условий; нет — основной расчёт. */
  print: { year?: number; variant?: 1 | 2; sensitivity?: string; conditions?: string };
}) {
  const [exportXlsx, { isLoading }] = useExportPrcXlsxMutation();
  const [error, setError] = useState<string | null>(null);

  async function onExport() {
    setError(null);
    try {
      const { url, name } = await exportXlsx({
        id: version.id,
        modelId,
        variant: print.conditions,
      }).unwrap();
      saveObjectUrl(url, name ?? `${safeFileName(`Калькуляция (форма) ${modelName}`)}.xlsx`);
    } catch (err) {
      setError(apiErrorText(err, 'Не удалось выгрузить форму по машине.'));
    }
  }

  return (
    <>
      <Button
        type="button"
        $variant="secondary"
        disabled={isLoading}
        title="Листы «Расчет машино-час» и «Калькуляция (форма)» по этой машине с блоками безубыточности"
        onClick={() => void onExport()}
      >
        <IconDownload size={16} />
        {isLoading ? 'Выгружаю…' : 'Форма по машине (Excel)'}
      </Button>
      <LinkButton
        to={printPath(version.id, modelId, print)}
        target="_blank"
        rel="noopener"
        title="Печатный вид «Калькуляции (форма)» в новой вкладке — оттуда печать или сохранение в PDF"
      >
        Печать / PDF
      </LinkButton>
      {error && (
        <Notice $tone="danger" role="alert">
          <span className="text">{error}</span>
        </Notice>
      )}
    </>
  );
}

function BodySkeleton() {
  return (
    <DrawerBody aria-busy="true">
      <SkeletonLine $height={64} />
      <SkeletonLine $width="60%" />
      <SkeletonLine $height={180} />
      <SkeletonLine $width="80%" />
    </DrawerBody>
  );
}

function History({ modelId }: { modelId: string }) {
  const { data, isLoading } = useGetPrcModelHistoryQuery(modelId);
  if (isLoading) return <SkeletonLine $width="50%" />;
  const items = data?.items ?? [];
  if (items.length === 0) {
    return (
      <Notice $tone="info">
        <span className="text">
          {typo('Утверждённых ПРЦ у модели ещё нет — в справочнике техники цены нет.')}
        </span>
      </Notice>
    );
  }
  return (
    <ScrollRegion label="Утверждённые ПРЦ модели">
      <Matrix>
        <caption>Утверждённые ПРЦ модели, свежие сверху</caption>
        <thead>
          <tr>
            <th scope="col">Действует</th>
            <th scope="col">Цена без НДС</th>
            <th scope="col">С НДС</th>
            <th scope="col">Версия</th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.id}>
              <th scope="row">
                с {formatIsoDate(p.valid_from)}
                {p.valid_to ? ` по ${formatIsoDate(p.valid_to)}` : ' · действует'}
              </th>
              <td>{formatServerMoney(p.price)}</td>
              <td>{formatServerMoney(p.price_with_vat)}</td>
              <td>
                <Link to={`/agents/econ/prc/${p.version_id}`}>{p.version_label ?? 'версия'}</Link> ·{' '}
                {p.project_name ? `объект «${p.project_name}»` : 'общая'} ·{' '}
                {personName(p.approved_by_name)}
              </td>
            </tr>
          ))}
        </tbody>
      </Matrix>
    </ScrollRegion>
  );
}

/**
 * Модель версии в боковой панели: цена года, статьи по годам, промежуточные
 * величины, норма ремонта по перечню лимитов, предупреждения, история
 * утверждённых цен и снимок паспорта, по которому шёл расчёт.
 */
export function ModelDrawer({
  versionId,
  version,
  conditions,
  modelId,
  preview,
  year,
  siblings,
  onSwitch,
  onClose,
  onBreakEven,
}: {
  versionId: string;
  version: PrcVersion;
  /** Вариант условий; нет — основной расчёт. */
  conditions?: string;
  modelId: string;
  preview: PrcModelRow | undefined;
  year: number;
  siblings: readonly string[];
  onSwitch: (id: string) => void;
  onClose: () => void;
  onBreakEven: (modelId: string) => void;
}) {
  const uid = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const { data, error, isFetching } = useGetPrcModelResultQuery({
    id: versionId,
    modelId,
    variant: conditions,
  });
  const model = data?.model.model_id === modelId ? data.model : undefined;
  const name = model?.model_name ?? preview?.model_name ?? 'Модель техники';
  const category = model?.category_name ?? preview?.category_name ?? null;
  const index = siblings.indexOf(modelId);
  const prev = index > 0 ? siblings[index - 1] : undefined;
  const next = index >= 0 && index < siblings.length - 1 ? siblings[index + 1] : undefined;
  const result = model?.result ?? null;
  const y = result?.years[String(year)];
  const limits = result?.repair_limits ?? preview?.repair_limits ?? null;
  const warnings = model?.warnings ?? preview?.warnings ?? [];
  const years = version.params.years;

  return (
    <Drawer open onClose={onClose} labelledBy={`${uid}-title`} size="lg" initialFocusRef={titleRef}>
      <SchemeScope>
        <DrawerHeader>
          <HeaderTop>
            <Eyebrow>{category ?? 'Модель версии'} · расчёт ПРЦ</Eyebrow>
            {siblings.length > 1 && index >= 0 && (
              <>
                <Position aria-hidden="true">
                  {index + 1} / {siblings.length}
                </Position>
                <DrawerIconButton
                  type="button"
                  aria-label="Предыдущая модель"
                  title="Предыдущая модель"
                  disabled={!prev}
                  onClick={() => prev && onSwitch(prev)}
                >
                  <PrevIcon size={18} />
                </DrawerIconButton>
                <DrawerIconButton
                  type="button"
                  aria-label="Следующая модель"
                  title="Следующая модель"
                  disabled={!next}
                  onClick={() => next && onSwitch(next)}
                >
                  <IconChevronRight size={18} />
                </DrawerIconButton>
              </>
            )}
            <DrawerCloseButton onClick={onClose} />
          </HeaderTop>
          <DrawerTitle id={`${uid}-title`} ref={titleRef} tabIndex={-1}>
            {name}
          </DrawerTitle>
          <MetaRow>
            {(model?.selected ?? preview?.selected) === false ? (
              <FleetChip $tone="muted">не утверждается</FleetChip>
            ) : (
              <FleetChip $tone="success">к утверждению</FleetChip>
            )}
            {limits && <FleetChip $tone="info">Ремонт по перечню лимитов</FleetChip>}
            {warnings.length > 0 && (
              <FleetChip $tone="warning">Предупреждений: {warnings.length}</FleetChip>
            )}
            <span className="text">
              {version.label} · {year} год
            </span>
          </MetaRow>
        </DrawerHeader>

        {error !== undefined && !model ? (
          <DrawerBody>
            <Notice $tone="danger" role="alert">
              <span className="text">
                {apiLoadErrorText(error, 'Не удалось открыть расчёт модели.', 'Нет доступа к ПРЦ.')}
              </span>
            </Notice>
          </DrawerBody>
        ) : !model ? (
          <BodySkeleton />
        ) : (
          <DrawerBody aria-busy={isFetching}>
            <Stack>
              {model.error && (
                <Notice $tone="danger" role="alert">
                  <span className="text">
                    <strong>ПРЦ не посчитана.</strong> {typo(model.error)}
                  </span>
                </Notice>
              )}
              {warnings.length > 0 && (
                <Notice $tone="warning">
                  <span className="text">
                    <strong>Предупреждения расчёта</strong>{' '}
                    {typo('— позиции нет в блоках, статья по ней посчитана нулём, как в Excel:')}
                  </span>
                  <WarnList>
                    {warnings.map((w) => (
                      <li key={w}>{typo(w)}</li>
                    ))}
                  </WarnList>
                </Notice>
              )}
            </Stack>

            {result && y && (
              <StatGrid $cols={4} aria-label={`Цена машино-часа ${year}`}>
                <div>
                  <dt>Цена без НДС, {year}</dt>
                  <dd className="big">{formatServerMoney(y.price)}</dd>
                </div>
                <div>
                  <dt>Цена с НДС</dt>
                  <dd className="big">{formatServerMoney(y.price_with_vat)}</dd>
                </div>
                <div>
                  <dt>Себестоимость</dt>
                  <dd className="big">{formatServerMoney(y.cost)}</dd>
                </div>
                <div>
                  <dt>Прибыль</dt>
                  <dd className="big">{formatServerMoney(y.profit)}</dd>
                </div>
              </StatGrid>
            )}

            {result && (
              <>
                <BlockSection aria-labelledby={`${uid}-years-h`}>
                  <BlockHeading id={`${uid}-years-h`}>Статьи по годам</BlockHeading>
                  <ArticlesByYear result={result} years={years} label="Статьи по годам" />
                </BlockSection>

                {limits && (
                  <BlockSection aria-labelledby={`${uid}-limits-h`}>
                    <BlockHeading id={`${uid}-limits-h`}>
                      Норма ремонта по перечню лимитов
                    </BlockHeading>
                    <StatGrid $cols={3}>
                      <div>
                        <dt>По перечню</dt>
                        <dd className="big">{formatPercent(limits.norm_pct, 2)}</dd>
                      </div>
                      <div>
                        <dt>В паспорте</dt>
                        <dd className="big">{formatPercent(limits.passport_norm_pct, 2)}</dd>
                      </div>
                      <div>
                        <dt>Итог перечня в год</dt>
                        <dd className="big">{formatServerMoney(limits.total)}</dd>
                      </div>
                    </StatGrid>
                    {limits.errors > 0 && (
                      <Notice $tone="warning">
                        <span className="text">
                          {typo(
                            `Ошибок в формулах перечня: ${limits.errors} — проверьте во вкладке «Лимиты на ремонт».`,
                          )}
                        </span>
                      </Notice>
                    )}
                  </BlockSection>
                )}

                <BlockSection aria-labelledby={`${uid}-mid-h`}>
                  <BlockHeading id={`${uid}-mid-h`}>Промежуточные величины</BlockHeading>
                  <IntermediateValues result={result} years={years} />
                </BlockSection>
              </>
            )}

            <BlockSection aria-labelledby={`${uid}-hist-h`}>
              <BlockHeading id={`${uid}-hist-h`}>Утверждённые цены</BlockHeading>
              <History modelId={modelId} />
            </BlockSection>

            <Snapshot>
              <summary>
                Снимок паспорта
                <IconChevronDown size={18} />
              </summary>
              <p>
                {typo(
                  'Исходные данные расчёта: паспорт справочника техники на момент создания версии или «Обновить паспорта». Правка паспорта в «Технике» сюда не попадает сама.',
                )}
              </p>
              <PassportView passport={model.input} idPrefix={`${uid}-p`} />
            </Snapshot>
          </DrawerBody>
        )}

        <DrawerFooter>
          <FooterActions>
            <Button type="button" $variant="secondary" onClick={() => onBreakEven(modelId)}>
              Безубыточность
            </Button>
            <span className="spacer" />
            <FormButtons
              version={version}
              modelId={modelId}
              modelName={name}
              print={{ year, conditions }}
            />
          </FooterActions>
        </DrawerFooter>
      </SchemeScope>
    </Drawer>
  );
}
